import { Controller } from "@hotwired/stimulus";

// IMPORTANT: do NOT `import { Turbo } from "@hotwired/turbo-rails"` here.
// The app already loads Turbo as a Sprockets module in
// app/views/layouts/application.html.haml. Pulling the npm package into
// the Webpacker bundle boots a SECOND Turbo Session, which dispatches
// `turbo:load` twice per navigation. The duplicate event re-runs
// `initCalendarComponents` in views/calendars/show.js, overwriting
// `window.calendarView` with a Vue root mounted on already-rendered DOM
// — it ends up with zero SFC children, which bricks the entire calendar
// app (table view shows "No Data Available" with stuck spinner,
// dropdowns dead, route-switcher clicks change the hash but not the
// view). Regression: registering this controller for the first time in
// PR #1008 made its import chain execute, double-booting Turbo.
//
// Use the globally-registered Turbo instead.

// How long we keep polling before showing the timeout state. The *window* is the
// invariant, not the poll count: callers set their own interval (creative analysis
// polls at 5s, welcome recommendations at the 10s default), so derive the count from
// it. A hardcoded count silently means a different window per caller — PR #827 set
// `maxPolls = 60; // 5 minutes at 5s intervals` for the 5s caller, which made the 10s
// caller wait 10 minutes while its copy promised 2-5.
const MAX_POLL_WINDOW_MS = 5 * 60 * 1000;

export default class extends Controller {
  static values = { url: String, interval: { type: Number, default: 10000 } };
  static targets = ["statusFrame"];

  connect() {
    this.pollCount = 0;
    this.maxPolls = Math.ceil(MAX_POLL_WINDOW_MS / this.intervalValue);
    this.startPolling();
    // Poll immediately on connect so users see progress right away
    this.poll();
  }

  disconnect() {
    this.stopPolling();
  }

  startPolling() {
    this.timer = setInterval(() => this.poll(), this.intervalValue);
  }

  stopPolling() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  async poll() {
    this.pollCount++;

    if (this.pollCount >= this.maxPolls) {
      this.stopPolling();
      this.showTimeout();
      return;
    }

    try {
      const response = await fetch(this.urlValue, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) return;

      const html = await response.text();
      // Turbo Streams will be processed automatically when inserted
      window.Turbo.renderStreamMessage(html);

      // After the stream replaces #creative-analysis-status, check whether
      // it was rendered in the "ready" state. If so, navigate to the full
      // analysis page (meta refresh inside a turbo-stream replacement does
      // not fire reliably, so we drive the navigation from here).
      const statusEl = document.getElementById("creative-analysis-status");
      if (
        statusEl &&
        statusEl.dataset.ready === "true" &&
        statusEl.dataset.redirectUrl
      ) {
        this.stopPolling();
        window.Turbo.visit(statusEl.dataset.redirectUrl);
      }
    } catch (_error) {
      // Silently retry on network errors
    }
  }

  retry() {
    this.pollCount = 0;
    // Re-show the loading state
    var timeoutEl = this.element.querySelector(
      ".recommendations-polling-timeout"
    );
    if (timeoutEl) timeoutEl.remove();
    var loadingEl = this.element.querySelector(
      ".recommendations-polling-loading"
    );
    if (loadingEl) loadingEl.classList.remove("hidden");
    this.startPolling();
  }

  showTimeout() {
    var loadingEl = this.element.querySelector(
      ".recommendations-polling-loading"
    );
    if (loadingEl) loadingEl.classList.add("hidden");
    var timeoutEl = this.element.querySelector(
      ".recommendations-polling-timeout"
    );
    if (timeoutEl) timeoutEl.classList.remove("hidden");
  }
}
